import { createContext, useContext } from 'react';
import { State as CobrandingState } from '../../reducers/cobranding-reducer';

import { ApplicationData, lookupApplication } from '../applications';
import { AidFrontendConfigCollection } from '../feature-flags/dynamic-config';
import { AidFrontendDynamicConfig } from '../../providers/DynamicConfigs';
import { checkFeatureGate } from '../feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../../providers/FeatureGates';
import { AnalyticsClient } from '../analytics/analytics-web-client';
import { checkAndActivateTWCG786Experiment } from '../../advanced-cobranding/growth/twcg-786/utils/experiment-utils';
import { checkDirectSignupExperiment } from '../../advanced-cobranding/trello/direct-signup/utills/direct-signups-experiment-check';
import { isDirectSignup } from '../../advanced-cobranding/trello/direct-signup/utills/isDirectSignup';

export enum SimpleCobranding {
  ADMIN = 'admin',
  ATLAS = 'atlas',
  BITBUCKET = 'bitbucket',
  COMMUNITY = 'community',
  COMPASS = 'compass',
  CONFLUENCE = 'confluence',
  JIRA = 'jira',
  JIRA_ALIGN = 'jira-align',
  LOOM = 'loom',
  NO_COBRANDING = 'no-cobranding',
  OPSGENIE = 'opsgenie',
  PRT = 'prt', // Primary Refresh Token testing
  ROVO = 'rovo',
  SAC = 'sac', // support.atlassian.com
  STATUSPAGE = 'statuspage',
  SUPPORT = 'support', // IDOASIS-350: Deprecate in favour of "sac"
  TRELLO = 'trello',
  WAC = 'wac', // www.atlassian.com
}

export enum AdvancedCobranding {
  ACCOUNT_LINKING = 'account-linking',
  ATLAS_66195 = 'atlas-66195',
  CFIND_2409 = 'cfind-2409',
  CONFLUENCE_ATLAS_75650 = 'confluence-atlas-75650',
  DEMO = 'demo',
  ENTRY_AUTH = 'entry-auth',
  CHAMPION_APPLY = 'champion-apply',
  CHAMPION_CONSENT = 'champion-consent',
  ISOLATED_CLOUD = 'ic',
  JIRA_ATLAS_75650 = 'jira-atlas-75650',
  LOOM = 'default',
  PRT_DEMO = 'prt-demo',
  SERVICE_COLLECTION = 'service-collection',
  TEAMWORK_COLLECTION = 'teamwork-collection',
  TRELLO = 'direct-signup',
  TWCG_1160_CONFLUENCE = 'twcg-1160-confluence',
  TWCG_1160_JIRA = 'twcg-1160-jira',
  TWCG_1293_FTA_OTP = 'twcg-1293-fta-otp',
  TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE = 'twcg-786-prioritise-sso',
  WAC_JPD = 'jpd',
  WAC_JSM = 'jsm',
  WAC_LOOM = 'loom-cobranding',
}

export const isSimpleCobranding = (value: string): value is SimpleCobranding =>
  Object.values<string>(SimpleCobranding).includes(value);

export const isAdvancedCobranding = (value: string): value is AdvancedCobranding =>
  Object.values<string>(AdvancedCobranding).includes(value);

const getDisabledApplicationKeys = (dynamicConfigs: AidFrontendConfigCollection) => {
  const disabledApplicationKeys = dynamicConfigs.getStringList(
    AidFrontendDynamicConfig.DISABLED_COBRANDING_APPLICATION_KEYS
  );

  if ('value' in disabledApplicationKeys) {
    return disabledApplicationKeys.value;
  }

  return [];
};

const getApplicationKey = (applicationData: ApplicationData) => {
  return `${applicationData.coreApplication}${
    applicationData.experience ? `--${applicationData.experience}` : ''
  }`;
};

export function getCurrentCobranding(
  cobranding: CobrandingState,
  dynamicConfigs: AidFrontendConfigCollection,
  analyticsClient?: AnalyticsClient | undefined
): SimpleCobranding | AdvancedCobranding {
  const applicationData = lookupApplication(cobranding);

  if (applicationData) {
    const disabledApplicationKeys = getDisabledApplicationKeys(dynamicConfigs);
    const applicationKey = getApplicationKey(applicationData);

    if (disabledApplicationKeys.includes(applicationKey)) {
      console.log(`Cobranding disabled for application: ${applicationKey}`);
      return SimpleCobranding.NO_COBRANDING;
    }

    switch (applicationData.coreApplication) {
      case 'admin':
        switch (applicationData.experience) {
          case 'jira-atlas-75650':
            return AdvancedCobranding.JIRA_ATLAS_75650;
          case 'confluence-atlas-75650':
            return AdvancedCobranding.CONFLUENCE_ATLAS_75650;
          default:
            if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
              return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
            }
            return SimpleCobranding.NO_COBRANDING;
        }
      case 'atlas':
        if (applicationData.experience === 'twcg-786-prioritise-sso') {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }

        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.ATLAS;
      case 'bitbucket':
        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.BITBUCKET;
      case 'community':
        switch (applicationData.experience) {
          case 'account-linking':
            return AdvancedCobranding.ACCOUNT_LINKING;
          case 'entry-auth':
            return AdvancedCobranding.ENTRY_AUTH;
          case 'champion-apply':
            return AdvancedCobranding.CHAMPION_APPLY;
          case 'champion-consent':
            return AdvancedCobranding.CHAMPION_CONSENT;
          default:
            if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
              return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
            }
            return SimpleCobranding.COMMUNITY;
        }
      case 'compass':
        return SimpleCobranding.COMPASS;
      case 'confluence':
        switch (applicationData.experience) {
          case 'twcg-1160':
            if (checkFeatureGate(AidFrontendFeatureGate.TWCG_1160_CONTEXTUAL_BACKGROUNDS)) {
              return AdvancedCobranding.TWCG_1160_CONFLUENCE;
            }
            return SimpleCobranding.CONFLUENCE;
          case 'mobile-ios-ic':
          case 'mobile-android-ic':
            return AdvancedCobranding.ISOLATED_CLOUD;
          default:
            if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
              return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
            }
            return SimpleCobranding.CONFLUENCE;
        }
      case 'jira':
        switch (applicationData.experience) {
          case 'twcg-1293-fta-otp':
            return AdvancedCobranding.TWCG_1293_FTA_OTP;
          case 'twcg-1160':
            if (checkFeatureGate(AidFrontendFeatureGate.TWCG_1160_CONTEXTUAL_BACKGROUNDS)) {
              return AdvancedCobranding.TWCG_1160_JIRA;
            }
            return SimpleCobranding.JIRA;
          case 'mobile-ios-ic':
          case 'mobile-android-ic':
            return AdvancedCobranding.ISOLATED_CLOUD;
          case 'twcg-786-prioritise-sso':
            return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
          default:
            if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
              return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
            }
            return SimpleCobranding.JIRA;
        }
      case 'jira-align':
        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.JIRA_ALIGN;
      case 'loom':
        if (applicationData.experience === 'default') {
          return AdvancedCobranding.LOOM;
        }
        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.LOOM;
      case 'mpac':
        if (applicationData.experience === 'ic') {
          return AdvancedCobranding.ISOLATED_CLOUD;
        }
        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.NO_COBRANDING;
      case 'opsgenie':
        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.OPSGENIE;
      case 'pollinator':
        if (applicationData.experience) {
          if (applicationData.experience.startsWith('jira')) {
            return SimpleCobranding.JIRA;
          }
          if (applicationData.experience.startsWith('confluence')) {
            return SimpleCobranding.CONFLUENCE;
          }
          if (applicationData.experience === 'demo') {
            return AdvancedCobranding.DEMO;
          }
        }
        return SimpleCobranding.NO_COBRANDING;
      case 'prt':
        if (applicationData.experience === 'demo') {
          return AdvancedCobranding.PRT_DEMO;
        }
        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.PRT;
      case 'rovo':
        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.ROVO;
      case 'sac':
        if (applicationData.experience === 'ic') {
          return AdvancedCobranding.ISOLATED_CLOUD;
        }
        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.NO_COBRANDING;
      case 'statuspage':
        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.STATUSPAGE;
      case 'support':
        if (applicationData.experience === 'ic') {
          return AdvancedCobranding.ISOLATED_CLOUD;
        }
        if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
          return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
        }
        return SimpleCobranding.NO_COBRANDING;
      case 'trello':
        switch (applicationData.experience) {
          case 'direct-signup':
            if (
              checkDirectSignupExperiment({ applicationData, analyticsClient }) &&
              isDirectSignup()
            ) {
              return AdvancedCobranding.TRELLO;
            }
            return SimpleCobranding.TRELLO;
          default:
            if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
              return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
            }
            return SimpleCobranding.TRELLO;
        }
      case 'wac':
        switch (applicationData.experience) {
          case 'otp-jira':
          case 'atlas-66195':
            return AdvancedCobranding.ATLAS_66195;
          case 'otp-confluence':
          case 'cfind-2409':
            return AdvancedCobranding.CFIND_2409;
          case 'loom':
            return AdvancedCobranding.WAC_LOOM;
          case 'jpd':
            return AdvancedCobranding.WAC_JPD;
          case 'jsm':
            return AdvancedCobranding.WAC_JSM;
          case 'service-collection':
            return AdvancedCobranding.SERVICE_COLLECTION;
          case 'teamwork-collection':
            return AdvancedCobranding.TEAMWORK_COLLECTION;
          case 'ic':
            return AdvancedCobranding.ISOLATED_CLOUD;
          default:
            if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
              return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
            }
            return SimpleCobranding.NO_COBRANDING;
        }
    }
  }

  if (checkAndActivateTWCG786Experiment(applicationData, analyticsClient)) {
    return AdvancedCobranding.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE;
  }
  return SimpleCobranding.NO_COBRANDING;
}

export interface CobrandingContext {
  cobranding: SimpleCobranding | AdvancedCobranding;
  isMobileApp: boolean;
}

const CobrandingContext = createContext<CobrandingContext>({
  cobranding: SimpleCobranding.NO_COBRANDING,
  isMobileApp: false,
});

export const CobrandingProvider = CobrandingContext.Provider;
export const CobrandingConsumer = CobrandingContext.Consumer;

export const useCobranding = () => useContext<CobrandingContext>(CobrandingContext);
